import * as React from "react";
import Typography from "@mui/material/Typography";
import { Avatar, Box, Button, Link } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router";
import startCase from "lodash/startCase";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { LoadingButton } from "@mui/lab";
import { useRef, useState } from "react";
import Form from "../../components/Form";
import { fields } from "../../models/Login";
import * as AuthenicationActions from "../../actions/authentication";
import { tl } from "../../components/translate";
import style from "./style.module.css";
import logo from "../../../assets/images/okhati_full_green.png";
import { RootState } from "../../store";
import { sessionOrLocalStorageUserData } from "../../auth/authentication";
import { isRequired, isValidEmail } from "../../helpers/validators";
import TwoFactorAuthQrScanModal from "./TwoFactorAuthQrScanModal";
import stringAvatar from "../../helpers/avatar";
import { resetUserPassword } from "../../api/user";
import { notificationAdd } from "../../actions/notification";
import ForgotPasswordModal from "./ForgotPasswordModal";
import hasOwnProperty from "../../helpers/object";

interface loginProps {
  login: (creds) => void;
}

const Login: React.FC<loginProps> = () => {
  const { required2FAToken, imageUrl } = useSelector((s: RootState) => s.twoFactorAuthInfo);
  const isLoggingIn = useSelector((state: RootState) => state.ongoing.LOGIN);
  const [state, setState] = React.useState<{
    credentials: {
      username: string;
      password: string;
      token: string;
    } | null;
    errorsCount: number;
    showErrors: boolean;
  }>({
    // eslint-disable-next-line no-bitwise
    credentials: { username: "", password: "", token: "" } || null,
    errorsCount: 0,
    showErrors: false
  });
  const selectedRCID = useRef(null);
  const [openForgotPasswordModal, setOpenForgotPasswordModal] = React.useState(false);
  const [emailForPwReset, setEmailForPwReset] = React.useState("");
  const onChange = (credentials, errorsCount) => {
    setState({ ...state, credentials, errorsCount });
  };
  const dispatch = useDispatch();
  const [rcList, setRcList] = useState([]);
  const onResetPassword = async (data = state.credentials) => {
    if (!data?.email || !emailForPwReset) {
      dispatch(
        notificationAdd({
          id: new Date().getTime(),
          message: "Email or Username is missing. Please check and try again!",
          variant: "error",
          autoTimeout: true
        })
      );
      return;
    }

    if (!isValidEmail(emailForPwReset)) {
      dispatch(
        notificationAdd({
          id: new Date().getTime(),
          message: "Email is invalid!",
          variant: "error",
          autoTimeout: true
        })
      );
      return;
    }
    try {
      await resetUserPassword({ username: data.email, email: emailForPwReset });
      dispatch(
        notificationAdd({
          id: new Date().getTime(),
          message: "Password reset successful. Please check your email.",
          variant: "success",
          autoTimeout: true
        })
      );
      setEmailForPwReset("");
      setOpenForgotPasswordModal(false);
    } catch (err) {
      dispatch(
        notificationAdd({
          id: new Date().getTime(),
          message: err.data?.message || "Something went wrong!",
          variant: "error",
          autoTimeout: true
        })
      );
    }
  };

  const isFromClinicsApp = window.isRnWebView || false;
  const { expoPushToken } = window;
  const isUserAuthenticated = useSelector((s: RootState) => s.authenticated);
  const isTokenRequired = required2FAToken && !imageUrl;
  const user = sessionOrLocalStorageUserData();

  const logIn = async (data = {} as { rcId: number; authenticable: string }) => {
    if (!hasOwnProperty(data, "rcId")) {
      if (!state.credentials?.username && !state.credentials?.password) {
        return;
      }
    }
    if (state.errorsCount) {
      setState({ ...state, showErrors: true });
    } else {
      dispatch(async (_, getState) => {
        await dispatch(
          AuthenicationActions.login({
            ...state.credentials,
            ...(data?.rcId ? { selectedRcId: data.rcId, authenticable: data.authenticable } : {}),
            ...(isFromClinicsApp ? { isFromClinicsApp, expoPushToken } : {})
          })
        );
        const { twoFactorAuthInfo } = getState();
        if (!twoFactorAuthInfo.required2FAToken && !twoFactorAuthInfo?.userAuthenticables?.length) {
          setState({ ...state, credentials: null });
        }
        if (twoFactorAuthInfo?.userAuthenticables?.length > 0) {
          setRcList(twoFactorAuthInfo?.userAuthenticables);
        }
        selectedRCID.current = null;
      });
    }
  };
  if (isUserAuthenticated && user) {
    return <Redirect to="/dashboard" />;
  }

  const updatedFields = [
    ...fields,
    ...(isTokenRequired
      ? [
          {
            key: "token",
            label: "token",
            value: "",
            inputType: "text",
            editable: true,
            creatable: true,
            required: true,
            validators: [isRequired({ msg: "Token is Required" })]
          }
        ]
      : [])
  ];

  return (
    <div className={style.root}>
      <div className={style.loginPanelBody}>
        <Box
          className={style.loginPanel}
          sx={{
            maxWidth: "500px",
            width: "100%",
            display: rcList?.length > 0 ? "block" : "none"
          }}
        >
          <div className={style.loginHeader}>
            <div className="login-logo-img">
              <img className={style.logo} src={logo} alt="Okhati Logo" />
            </div>
            <Typography variant="caption" gutterBottom className={style.subText}>
              Select Hospital/Clinic
            </Typography>
          </div>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {rcList.map((item) => (
              <Button
                loading={item.id === selectedRCID.current}
                disabled={isLoggingIn}
                key={item.id}
                sx={{
                  color: "grey",
                  textAlign: "left",
                  justifyContent: "flex-start",
                  textTransform: "none"
                }}
                onClick={() => {
                  selectedRCID.current = item.id;
                  logIn({ rcId: item.resourceCentre.id, authenticable: item.authenticable });
                }}
              >
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Avatar
                    src={item.resourceCentre?.resourceCentreLogo?.s3ResourceURL}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...stringAvatar(item.resourceCentre.name)}
                    sx={{ width: 30, height: 30 }}
                  />
                  <div>
                    <Box display="flex" gap={1} alignItems="flex-end">
                      <Typography variant="body1">{item.resourceCentre.name}</Typography>
                      <Typography variant="caption">({item.resourceCentre.address})</Typography>
                    </Box>
                    <Typography variant="caption">{startCase(item.userGroups[0]?.name)}</Typography>
                  </div>
                </Box>
              </Button>
            ))}

            <Button
              onClick={() => {
                setRcList([]);
              }}
              disabled={isLoggingIn}
              variant="outlined"
              sx={{ mt: 2 }}
            >
              Back to login
            </Button>
          </Box>
        </Box>
        <Box
          sx={{
            display: rcList?.length > 0 ? "none" : "block"
          }}
        >
          <Form
            name="loginForm"
            title={
              <div className={style.loginHeader}>
                <div className="login-logo-img">
                  <img className={style.logo} src={logo} alt="Okhati Logo" />
                </div>
                <Typography variant="caption" gutterBottom className={style.subText}>
                  आफ्नो Okhati खाता प्रयोग गरी भित्र पस्नुहोस
                </Typography>
              </div>
            }
            classNames={{
              root: style.loginFormRoot,
              paper: style.loginPanel
            }}
            onChange={(data, errors, errorsCount) => onChange(data, errorsCount)}
            data={{}}
            fields={updatedFields}
            hideSaveButton
            showErrors={state.showErrors}
            onSave={() => logIn()}
            footer={
              <div>
                <Typography
                  sx={{ cursor: "pointer" }}
                  onClick={() => {
                    if (!state.credentials?.email) {
                      dispatch(
                        notificationAdd({
                          id: new Date().getTime(),
                          message: "Please enter username to continue!",
                          variant: "error",
                          autoTimeout: true
                        })
                      );
                    } else {
                      setOpenForgotPasswordModal(true);
                    }
                  }}
                >
                  Forgot Password?
                </Typography>
                <Box sx={{ my: 1, display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Link
                    title="Download Okhati Clinics App From Playstore"
                    target="_blank"
                    href="https://play.google.com/store/apps/details?id=com.okhati.appforclinics&hl=en&pli=1"
                    variant="caption"
                    sx={{ display: "flex", alignItems: "flex-start", fontWeight: "bold" }}
                  >
                    Download Okhati Clinics App
                    <OpenInNewIcon sx={{ fontSize: 8 }} />
                  </Link>
                  <Link
                    title="Free ICD-11 Brower With Treeview And Search"
                    target="_blank"
                    href="https://okhati.com.np/icd"
                    variant="caption"
                    sx={{ display: "flex", alignItems: "flex-start", fontWeight: "bold" }}
                  >
                    ICD-11 Browser
                    <OpenInNewIcon sx={{ fontSize: 8 }} />
                  </Link>
                </Box>

                <div className={style.loginButton}>
                  <LoadingButton
                    loading={isLoggingIn}
                    variant="contained"
                    data-testmation="loginButton"
                    aria-label="Login"
                    type="submit"
                    form="loginForm"
                    color="primary"
                    className={style.loginButton}
                    onClick={() => logIn()}
                    disabled={isTokenRequired && !state.credentials?.token}
                  >
                    {isTokenRequired ? tl("main.verify") : tl("main.login")}
                  </LoadingButton>
                </div>
              </div>
            }
          />
          <ForgotPasswordModal
            isOpen={openForgotPasswordModal}
            onClose={() => {
              setOpenForgotPasswordModal(false);
              setEmailForPwReset("");
            }}
            email={emailForPwReset}
            setEmail={setEmailForPwReset}
            onResetPassword={onResetPassword}
          />
          <TwoFactorAuthQrScanModal
            isLoading={isLoggingIn}
            onSave={(token) => logIn({ ...state.credentials, token })}
          />
        </Box>
      </div>
    </div>
  );
};

export default Login;
